import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Empty, Kpi, KpiGrid, Muted, PageHeader, Section } from '@/components/ui'
import {
  importarEmpresasArquivo,
  type ImportarEmpresasProgresso,
  type ImportarEmpresasResult,
} from './api'

export function ImportarEmpresasPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('empresas')
  const qc = useQueryClient()

  const [file, setFile] = useState<File | null>(null)
  const [dryRun, setDryRun] = useState(true)
  const [criarFaltantes, setCriarFaltantes] = useState(false)
  const [vincular, setVincular] = useState(true)
  const [msg, setMsg] = useState('')
  const [erro, setErro] = useState(false)
  const [pending, setPending] = useState(false)
  const [progresso, setProgresso] = useState<ImportarEmpresasProgresso | null>(null)
  const [resultado, setResultado] = useState<ImportarEmpresasResult | null>(null)

  async function executar(opts: { atualizarEditadas?: boolean } = {}) {
    if (!file) {
      setErro(true)
      setMsg('Selecione o arquivo empresas.wk1.')
      return
    }
    const atualizarEditadas = Boolean(opts.atualizarEditadas)
    setPending(true)
    setErro(false)
    setMsg(
      atualizarEditadas
        ? 'Atualizando empresas editadas com dados do Domínio…'
        : 'Enviando arquivo…',
    )
    setProgresso(null)
    setResultado(null)
    try {
      const res = await importarEmpresasArquivo(file, {
        dryRun: atualizarEditadas ? false : dryRun,
        criarFaltantes,
        vincular: atualizarEditadas ? false : !dryRun && vincular,
        atualizarEditadas,
        onProgress: (p) => {
          setProgresso(p)
          setMsg(
            `Processando lote ${p.loteAtual}/${p.lotes} — ${p.done} de ${p.total} empresas…`,
          )
        },
      })
      setResultado(res)
      setProgresso({
        done: res.total ?? res.lidos ?? 0,
        total: res.total ?? res.lidos ?? 0,
        atualizados: res.atualizados ?? 0,
        criados: res.criados ?? 0,
        nao_casados: res.nao_casados ?? 0,
        inativos: res.inativos ?? 0,
        divergentes: res.divergentes ?? 0,
        loteAtual: 1,
        lotes: 1,
      })
      const div = res.divergentes ?? 0
      const vinCol = res.vinculo_colaboradores
      const vinCar = res.vinculo_cargos
      const vinMsg =
        vinCol || vinCar
          ? ` Colaboradores vinculados: ${vinCol?.vinculados ?? 0} (pend. ${vinCol?.pendentes ?? 0}). Cargos: ${vinCar?.vinculados ?? 0}.`
          : ''
      const divMsg = div
        ? ` ${div} empresa(s) editada(s) localmente com divergência — confirme abaixo se quiser sobrescrever.`
        : ''
      setMsg(
        res.dry_run
          ? `Simulação concluída — nada foi gravado.${divMsg}`
          : atualizarEditadas
            ? `Divergentes atualizados com o Domínio.${vinMsg}`
            : `Importação de empresas concluída.${divMsg}${vinMsg}`,
      )
      if (!res.dry_run) {
        await qc.invalidateQueries({ queryKey: ['rh', 'empresas'] })
        await qc.invalidateQueries({ queryKey: ['rh', 'colaboradores'] })
        await qc.invalidateQueries({ queryKey: ['rh', 'cargos'] })
      }
    } catch (err) {
      setResultado(null)
      setErro(true)
      setMsg(err instanceof ApiError ? err.message : 'Falha na importação de empresas.')
    } finally {
      setPending(false)
    }
  }

  if (!editable) {
    return (
      <section className="hi-page">
        <PageHeader title="Importar empresas" subtitle="Acesso restrito a RH/Admin." />
        <Alert tone="danger">Sem permissão para importar empresas.</Alert>
      </section>
    )
  }

  const detalhes = resultado?.detalhes || []
  const naoCasados = detalhes.filter((d) => d.status === 'nao_casado')
  const okDet = detalhes.filter((d) => d.status === 'ok' || d.status === 'criado' || d.campos?.length)
  const divergentesDet = detalhes.filter((d) => d.status === 'divergente')
  const inativosDet = detalhes.filter((d) => d.status === 'ignorado_inativo')
  const pct =
    progresso && progresso.total > 0
      ? Math.min(100, Math.round((progresso.done / progresso.total) * 100))
      : pending
        ? 2
        : 0

  return (
    <section className="hi-page hi-import-colab-page">
      <PageHeader
        title="Importar empresas"
        subtitle="Domínio WK1 — só sincroniza empresas ativas (stat_emp = A). Inativas são ignoradas. Editadas localmente não são sobrescritas sem confirmação."
        actions={
          <Link className="btn btn-secondary" to="/app/empresas">
            Voltar à lista
          </Link>
        }
      />

      {msg ? <Alert tone={erro ? 'danger' : 'info'}>{msg}</Alert> : null}

      <Section title="Arquivo">
        <form
          className="hi-filters hi-import-form"
          onSubmit={(e) => {
            e.preventDefault()
            void executar()
          }}
        >
          <input
            type="file"
            accept=".wk1,.csv,.xls,.xlsx"
            aria-label="Arquivo de empresas Domínio"
            disabled={pending}
            onChange={(e) => {
              setFile(e.target.files?.[0] || null)
              setResultado(null)
              setProgresso(null)
              setMsg('')
              setErro(false)
            }}
          />
          <label className="hi-check">
            <input
              type="checkbox"
              checked={dryRun}
              disabled={pending}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Simular (dry-run)
          </label>
          <label className="hi-check">
            <input
              type="checkbox"
              checked={criarFaltantes}
              disabled={pending}
              onChange={(e) => setCriarFaltantes(e.target.checked)}
            />
            Criar quem não existir
          </label>
          <label className="hi-check">
            <input
              type="checkbox"
              checked={vincular}
              disabled={pending || dryRun}
              onChange={(e) => setVincular(e.target.checked)}
            />
            Vincular colaboradores e cargos (codi_emp)
          </label>
          <button type="submit" className="btn btn-primary" disabled={pending || !file}>
            {pending ? 'Importando…' : dryRun ? 'Simular importação' : 'Importar'}
          </button>
        </form>
        <Muted>
          {file
            ? `Selecionado: ${file.name} (${Math.round(file.size / 1024)} KB)`
            : 'Ex.: empresas.wk1 (Domínio)'}
        </Muted>

        {(pending || progresso) && (
          <div className="hi-import-progress" aria-live="polite">
            <div className="hi-import-progress-meta">
              <span>
                {progresso
                  ? `${progresso.done} / ${progresso.total} (${pct}%)`
                  : 'Preparando…'}
              </span>
              {progresso ? (
                <span>
                  Lote {progresso.loteAtual}/{progresso.lotes}
                </span>
              ) : null}
            </div>
            <div
              className="hi-import-progress-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
            >
              <div className="hi-import-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            {progresso ? (
              <Muted>
                Atualizados {progresso.atualizados} · Criados {progresso.criados} · Sem match{' '}
                {progresso.nao_casados} · Divergentes {progresso.divergentes} · Inativos{' '}
                {progresso.inativos}
              </Muted>
            ) : null}
          </div>
        )}
      </Section>

      {resultado ? (
        <>
          <KpiGrid>
            <Kpi label="Lidos" value={String(resultado.lidos ?? resultado.total ?? 0)} />
            <Kpi label="Atualizados" value={String(resultado.atualizados ?? 0)} />
            <Kpi label="Criados" value={String(resultado.criados ?? 0)} />
            <Kpi label="Divergentes" value={String(resultado.divergentes ?? 0)} />
            <Kpi label="Sem match" value={String(resultado.nao_casados ?? 0)} />
          </KpiGrid>

          {divergentesDet.length > 0 ? (
            <Section title="Divergências (editadas localmente)">
              <Alert tone="info">
                Estas empresas foram editadas no Hi Conecta. O Domínio trouxe valores diferentes.
                Deseja sobrescrever com o arquivo?
              </Alert>
              <div className="hi-import-detalhes">
                <ul>
                  {divergentesDet.slice(0, 40).map((d, i) => (
                    <li key={`div-${i}`}>
                      <strong>{d.nome}</strong>
                      {d.codigo ? ` [${d.codigo}]` : ''}
                      {d.divergencias?.length ? (
                        <ul>
                          {d.divergencias.slice(0, 8).map((diff, j) => (
                            <li key={`d-${i}-${j}`}>
                              {diff.campo}: “{diff.atual || '—'}” → “{diff.novo || '—'}”
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
              {!resultado.dry_run && file ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={pending}
                  onClick={() => {
                    if (
                      confirm(
                        `Atualizar ${divergentesDet.length} empresa(s) editada(s) com os dados do Domínio?`,
                      )
                    ) {
                      void executar({ atualizarEditadas: true })
                    }
                  }}
                >
                  Sim, atualizar divergentes
                </button>
              ) : (
                <Muted>Desmarque dry-run e importe de novo para poder confirmar a atualização.</Muted>
              )}
            </Section>
          ) : null}

          <Section title="Detalhes" flush>
            {!detalhes.length ? <Empty>Sem detalhes neste lote.</Empty> : null}
            {okDet.length > 0 ? (
              <div className="hi-import-detalhes">
                <Muted>Atualizações / criações (amostra)</Muted>
                <ul>
                  {okDet.slice(0, 40).map((d, i) => (
                    <li key={`ok-${i}`}>
                      <strong>{d.nome}</strong>
                      {d.codigo ? ` [${d.codigo}]` : ''}
                      {d.campos?.length ? ` — ${d.campos.join(', ')}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {naoCasados.length > 0 ? (
              <div className="hi-import-detalhes">
                <Muted>Sem match (amostra)</Muted>
                <ul>
                  {naoCasados.slice(0, 40).map((d, i) => (
                    <li key={`nc-${i}`}>
                      {d.nome}
                      {d.codigo ? ` [${d.codigo}]` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {inativosDet.length > 0 ? (
              <div className="hi-import-detalhes">
                <Muted>Inativas no Domínio — não sincronizadas (amostra)</Muted>
                <ul>
                  {inativosDet.slice(0, 40).map((d, i) => (
                    <li key={`in-${i}`}>
                      {d.nome}
                      {d.codigo ? ` [${d.codigo}]` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Section>
        </>
      ) : null}
    </section>
  )
}
